import type { TodoAlarm } from '@/api'
import { formatTemporalLong } from './dates'
import { currentLocale, i18n } from '../i18n'

export interface AlarmPreset {
  labelKey: string
  trigger: string
}

// Relative to the due date (RELATED=END), which is what todo reminders are usually about.
export const ALARM_PRESETS: AlarmPreset[] = [
  { labelKey: 'alarm.preset.atDue', trigger: 'PT0S' },
  { labelKey: 'alarm.preset.min5', trigger: '-PT5M' },
  { labelKey: 'alarm.preset.min15', trigger: '-PT15M' },
  { labelKey: 'alarm.preset.min30', trigger: '-PT30M' },
  { labelKey: 'alarm.preset.hour1', trigger: '-PT1H' },
  { labelKey: 'alarm.preset.day1', trigger: '-P1D' },
  { labelKey: 'alarm.preset.week1', trigger: '-P1W' },
]

const DURATION = /^([+-])?P(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/

export function durationSeconds(trigger: string): number | null {
  const match = DURATION.exec(trigger.trim().toUpperCase())
  if (!match) return null
  const [, sign, w, d, h, m, s] = match
  const total =
    Number(w ?? 0) * 604800 +
    Number(d ?? 0) * 86400 +
    Number(h ?? 0) * 3600 +
    Number(m ?? 0) * 60 +
    Number(s ?? 0)
  return sign === '-' ? -total : total
}

// "15 minutes" in the UI language, with that language's plural forms.
function amount(n: number, unit: Intl.NumberFormatOptions['unit']): string {
  return new Intl.NumberFormat(currentLocale(), { style: 'unit', unit, unitDisplay: 'long' }).format(n)
}

export function describeAlarm(alarm: TodoAlarm): string {
  const seconds = durationSeconds(alarm.trigger)
  if (seconds === null) return formatTemporalLong(alarm.trigger) || alarm.trigger
  const anchor = alarm.related === 'START' ? 'Start' : 'Due'
  const { t } = i18n.global
  if (seconds === 0) return t(`alarm.at${anchor}Time`)
  const abs = Math.abs(seconds)
  const units: [number, Intl.NumberFormatOptions['unit']][] = [
    [604800, 'week'],
    [86400, 'day'],
    [3600, 'hour'],
    [60, 'minute'],
    [1, 'second'],
  ]
  const [size, unit] = units.find(([size]) => abs % size === 0) ?? [1, 'second']
  return t(`alarm.${seconds < 0 ? 'before' : 'after'}${anchor}`, { amount: amount(abs / size, unit) })
}
